import { FormGroup, FormControl, Validators } from '@angular/forms';
import { UserDetails } from '../user-details.model';
import { firestore } from 'firebase';
import * as moment from 'moment';

export class UserDetailsForm {
  public formGroup: FormGroup;

  get isServiceProvider(): boolean {
    return this.formGroup.value.isServiceProvider;
  }

  get isRSACitizen(): boolean {
    return this.formGroup.value.rsaCitizen;
  }

  constructor(userDetails: UserDetails) {
    this.initializeForm();
    this.updateForm(userDetails);
  }

  get model(): UserDetails {
    const model = new UserDetails();
    model.profileComplete = true;
    if (this.formGroup.value?.firstName) {
      model.firstName = this.formGroup.value?.firstName;
    }
    if (this.formGroup.value?.lastName) {
      model.lastName = this.formGroup.value?.lastName;
    }
    if (this.formGroup.value?.phoneNumber) {
      model.phoneNumber = this.formGroup.value?.phoneNumber;
    }
    if (this.formGroup.value?.emailAddress) {
      model.emailAddress = this.formGroup.value?.emailAddress;
    }
    if (this.formGroup.value?.dateOfBirth) {
      console.log(this.formGroup.value?.dateOfBirth);
      model.dateOfBirth = firestore.Timestamp.fromDate(
        this.formGroup.value?.dateOfBirth?.toDate()
      );
    }

    model.isServiceProvider = this.formGroup.value?.isServiceProvider;

    if (this.formGroup.value?.rsaCitizen !== undefined) {
      model.rsaCitizen = this.formGroup.value?.rsaCitizen;
    }
    if (this.formGroup.value?.rsaIdNumber) {
      model.rsaIdNumber = this.formGroup.value?.rsaIdNumber ?? null;
    }
    if (this.formGroup.value?.passportNumber) {
      model.passportNumber = this.formGroup.value?.passportNumber ?? null;
    }
    if (this.formGroup.value?.addressStreet1) {
      model.addressStreet1 = this.formGroup.value?.addressStreet1;
    }
    if (this.formGroup.value?.addressStreet2) {
      model.addressStreet2 = this.formGroup.value?.addressStreet2;
    }
    if (this.formGroup.value?.addressCity) {
      model.addressCity = this.formGroup.value?.addressCity;
    }
    if (this.formGroup.value?.addressPostCode) {
      model.addressPostCode = this.formGroup.value?.addressPostCode;
    }
    return model;
  }

  get validators() {
    return {
      firstName: [
        {
          type: 'required',
          message: 'First name is required.'
        }
      ],
      lastName: [
        {
          type: 'required',
          message: 'Last name is required.'
        }
      ],
      phoneNumber: [
        {
          type: 'required',
          message: 'Phone number is required.'
        },
        {
          type: 'pattern',
          message: 'Please enter a valid phone number.'
        }
      ],
      emailAddress: [
        {
          type: 'required',
          message: 'Email address is required.'
        },
        {
          type: 'email',
          message: 'Please enter a valid email address.'
        }
      ],
      rsaIdNumber: [
        {
          type: 'required',
          message: 'ID Number is required.'
        },
        { type: 'pattern', message: 'Please enter a valid ID number' }
      ],
      passportNumber: [
        {
          type: 'required',
          message: 'Passport Number is required.'
        }
      ],
      addressStreet1: [
        {
          type: 'required',
          message: 'Address is required.'
        }
      ],
      addressCity: [
        {
          type: 'required',
          message: 'City is required.'
        }
      ],
      addressPostCode: [
        {
          type: 'required',
          message: 'Postcode is required.'
        }
      ]
    };
  }

  public updateForm(userDetails: any) {
    if (!!userDetails) {
      this.formGroup.patchValue(userDetails);

      this.formGroup.patchValue({
        dateOfBirth: moment(userDetails?.dateOfBirth?.toDate())
      });
    }
  }

  private initializeForm() {
    this.formGroup = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{9,10}$')
      ]),
      emailAddress: new FormControl('', [
        Validators.required,
        Validators.email
      ]),
      dateOfBirth: new FormControl('', []),
      isServiceProvider: new FormControl(false, []),
      rsaCitizen: new FormControl({ value: true, disabled: true }, [
        Validators.required
      ]),
      rsaIdNumber: new FormControl({ value: '', disabled: true }, [
        Validators.required
      ]),
      passportNumber: new FormControl({ value: '', disabled: true }, [
        Validators.required
      ]),
      addressStreet1: new FormControl({ value: '', disabled: true }, [
        Validators.required
      ]),
      addressStreet2: new FormControl({ value: '', disabled: true }),
      addressCity: new FormControl({ value: '', disabled: true }, [
        Validators.required
      ]),
      addressPostCode: new FormControl({ value: '', disabled: true }, [
        Validators.required
      ])
    });

    this.formGroup.controls.isServiceProvider.valueChanges.subscribe(
      isServiceProvider => this.isServiceProviderChanged(isServiceProvider)
    );

    this.formGroup.controls.rsaCitizen.valueChanges.subscribe(rsaCitizen =>
      this.isRSACitizenChanged(rsaCitizen)
    );
  }

  private isServiceProviderChanged(isServiceProvider: boolean) {
    if (isServiceProvider) {
      this.formGroup.controls.rsaCitizen.enable();
      this.formGroup.controls.rsaCitizen.patchValue(true);
      this.formGroup.controls.rsaCitizen.updateValueAndValidity();

      this.formGroup.controls.addressStreet1.enable();
      this.formGroup.controls.addressStreet2.enable();
      this.formGroup.controls.addressCity.enable();
      this.formGroup.controls.addressPostCode.enable();
    } else {
      this.formGroup.controls.rsaCitizen.disable();
      this.formGroup.controls.rsaCitizen.updateValueAndValidity();

      this.formGroup.controls.addressStreet1.disable();
      this.formGroup.controls.addressStreet2.disable();
      this.formGroup.controls.addressCity.disable();
      this.formGroup.controls.addressPostCode.disable();
    }
  }

  private isRSACitizenChanged(isRSACitizen: boolean) {
    if (isRSACitizen) {
      this.formGroup.controls.rsaIdNumber.enable();
      this.formGroup.controls.passportNumber.disable();
    } else {
      this.formGroup.controls.rsaIdNumber.disable();
      this.formGroup.controls.passportNumber.enable();
    }
  }
}
